import React, { useState, useEffect, forwardRef, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ReactImageFallback from 'react-image-fallback'

import { addingHandlerAction, setCurrency, resetOrderAndCart } from 'actions'
import { CloseIcon, Button } from 'components/common'
import {
  sortOptionsByOptionNameHandler,
  sortOptionsSetByItemId,
  comparisonIdGenerator
} from '../../utils'
import CheckBoxOptions from './CheckBoxOptions'
import PlusMinusOptions from './PlusMinusOptions'
import s from './OrderModal.module.css'
import { toast } from 'react-toastify'
import DifferentBusinessErrorModal from 'components/OrderModal/DifferentBusinessErrorModal'

const OrderModal = forwardRef(({ selectedItem }, itemInfoModalRef) => {
  const { data: businessData } = useSelector((state) => state.restaurantDetails)
  const differentBusinessErrorModal = useRef(null)

  const [price, setPrice] = useState(selectedItem.price)
  const [allSelectedOptions, setAllSelectedOptions] = useState([])
  const [requireCheckArr, setRequireCheckArr] = useState([])

  const dispatch = useDispatch()

  useEffect(() => {
    setAllSelectedOptions([])
  }, [selectedItem])

  useEffect(() => {
    const valuesWithCosts = allSelectedOptions.flatMap(
      (option) => option.optionSet
    )

    let tempPrice = selectedItem.price

    if (valuesWithCosts.length)
      valuesWithCosts.forEach((value) => {
        if (value.additionalCost) {
          tempPrice += value.additionalCost * value.quantity
        }
      })

    setPrice(tempPrice)
  }, [allSelectedOptions])

  const addItemsToCartHandler = () => {
    if (!businessData.open && !businessData.accepts_orders_when_closed) {
      toast.error('This business is now closed')
      return
    }

    // If any of item isn't decent, then pop an error modal
    const undecentItem = requireCheckArr.find((item) => !item.decent)

    if (undecentItem) {
      toast.error(
        `You must make selections in the ${undecentItem.itemName} category`
      )
      return
    }

    const sortedOptionsByOptionName =
      sortOptionsByOptionNameHandler(allSelectedOptions)

    const sortedOption = sortedOptionsByOptionName.map((option) => ({
      ...option,
      optionSet: sortOptionsSetByItemId(option.optionSet)
    }))

    const comparisonId = comparisonIdGenerator(sortedOption, selectedItem.id)

    try {
      dispatch(
        addingHandlerAction(businessData.id, {
          itemId: selectedItem.id,
          itemName: selectedItem.name,
          cost: price,
          quantity: 1,
          optionSetItems: allSelectedOptions,
          comparisonId
        })
      )
    } catch (error) {
      differentBusinessErrorModal.current.openModal()
      return
    }

    dispatch(setCurrency(selectedItem.currency))

    itemInfoModalRef.current.closeModal()
  }

  return (
    <>
      <DifferentBusinessErrorModal
        ref={differentBusinessErrorModal}
        buttonNode={
          <Button
            text="Clear and Set"
            onClick={() => {
              dispatch(resetOrderAndCart())
              addItemsToCartHandler()
              itemInfoModalRef.current.closeModal()
            }}
          />
        }
      />

      <div className="pb-[60px] md:pb-[100px] overflow-hidden overflow-y-scroll orderWrapper">
        <div className={s.headerWrapper}>
          <h3 className={s.itemTitle}>
            {!!selectedItem?.options.length && (
              <span className="mr-2">Select options -</span>
            )}

            {selectedItem.name}
          </h3>
          <CloseIcon
            style={{ cursor: 'pointer' }}
            onClick={() => itemInfoModalRef.current.closeModal()}
          />
        </div>

        <div className={s.itemInfoWrapper}>
          {selectedItem.image && (
            <ReactImageFallback
              src={selectedItem.image}
              fallbackImage={<> </>}
              className={s.itemImg}
            />
          )}

          {selectedItem.description && (
            <p className={s.description}>{selectedItem.description}</p>
          )}

          <div className={s.descriptionWrapper}>
            {selectedItem.options?.length
              ? selectedItem.options?.map((optionData) => (
                  <>
                    <div className={s.optionDescription}>
                      {optionData.description}
                    </div>

                    <h4 className={s.optionName}>{optionData.name}</h4>

                    {optionData.required ? (
                      <CheckBoxOptions
                        optionData={optionData}
                        allSelectedOptions={allSelectedOptions}
                        setAllSelectedOptions={setAllSelectedOptions}
                        requireCheckArr={requireCheckArr}
                        setRequireCheckArr={setRequireCheckArr}
                        currency={selectedItem.currency}
                      />
                    ) : (
                      <PlusMinusOptions
                        optionData={optionData}
                        allSelectedOptions={allSelectedOptions}
                        setAllSelectedOptions={setAllSelectedOptions}
                        requireCheckArr={requireCheckArr}
                        setRequireCheckArr={setRequireCheckArr}
                        currency={selectedItem.currency}
                      />
                    )}
                  </>
                ))
              : null}
          </div>

          <div className={s.addToCardRow}>
            <Button
              onClick={() => itemInfoModalRef.current.closeModal()}
              style={{ marginRight: '20px', width: '40%' }}
              text="Cancel"
            />
            <Button
              onClick={addItemsToCartHandler}
              text={`Add for ${selectedItem.currency}${price}`}
            />
          </div>
        </div>
      </div>
    </>
  )
})

export default OrderModal
